import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookOpen, Save, Loader2 } from "lucide-react";
import api from "../lib/api";

const PLACEHOLDER = `Teach the AI how this project runs. Examples:

• How dispatch works in RoadRunner — driver radius, surge logic, escalation
• Where the deploy pipeline lives and how a release is cut
• Tone and brand voice for outbound messages
• On-call runbook: what to check when the site is slow

The concierge will use this as its operating manual.`;

const KnowledgeBaseEditor = ({ app, onUpdated }) => {
  const [value, setValue] = useState(app.knowledge_base || "");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setValue(app.knowledge_base || "");
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: reset only when switching app
  }, [app.id]);

  const handleChange = (e) => {
    setValue(e.target.value);
    setDirty(e.target.value !== (app.knowledge_base || ""));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/apps/${app.id}`, { knowledge_base: value });
      onUpdated?.(data);
      setDirty(false);
      toast.success("The concierge has studied your notes.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const words = value.trim() ? value.trim().split(/\s+/).length : 0;

  return (
    <div className="regal-card overflow-hidden" data-testid="kb-editor">
      <div className="px-6 py-4 border-b border-seam flex items-center justify-between bg-elevated/40">
        <div className="flex items-center gap-3">
          <BookOpen className="w-4 h-4 text-champagne" strokeWidth={1.5} />
          <div>
            <p className="label-eyebrow">Knowledge Base</p>
            <h3 className="font-heading text-2xl mt-1">Teach the concierge</h3>
          </div>
        </div>
        <span className="text-xs text-textmuted">{words} words</span>
      </div>

      <div className="px-6 py-5">
        <textarea
          value={value}
          onChange={handleChange}
          placeholder={PLACEHOLDER}
          data-testid="kb-textarea"
          className="w-full min-h-[360px] bg-obsidian border border-seam rounded-md px-5 py-4 text-textmain leading-relaxed focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40 transition-colors font-sans text-sm placeholder-[#52525B] resize-y"
        />
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-textmuted">
            {dirty ? "Unsaved changes" : "All changes saved"}
          </span>
          <button
            type="button"
            onClick={save}
            disabled={!dirty || saving}
            data-testid="kb-save-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-champagne text-obsidian font-medium hover:bg-champagne-soft transition-all shadow-[0_0_15px_rgba(212,175,55,0.15)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" strokeWidth={1.5} />
            )}
            Save knowledge
          </button>
        </div>
      </div>
    </div>
  );
};

export default KnowledgeBaseEditor;
